import SyncableModel from "../../database/SyncableModel"
import { CycleDisease } from "../../database"
import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { withBaseModel } from "../extensions/with-base-model"
import { BaseModelStoreProps } from "../base-model-store/base-model-store"
import JalaAnalytic from "../../utils/jala-analytic"

const CycleDiseaseStoreProps = {
  ...BaseModelStoreProps
}

/**
 * Model description here for TypeScript hints.
 */
export const CycleDiseaseStoreModel = types
  .model("CycleDiseaseStore")
  .props(CycleDiseaseStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .extend(withBaseModel)
  .views((self) => ({})) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof CycleDiseaseStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    init() {
      self.initModel();
    },
    async store(data) {
      if (self.rootStore.appStore?.isOffline)
        return this.storeOffline(data)
      return self.storeModel(CycleDisease, data, false, async (success: boolean, model: SyncableModel) => {
          const { fileStore } = self.rootStore
          for (let i = 0; i < (data.files || []).length; i++) {
            if (!data.files[i]) continue;
            await fileStore.store({ fileable_id: model.getKey(), ...data.files[i] })
          }
        })
        .then((success: boolean) => {
          const { files, ...restData } = data;
          if (success) JalaAnalytic.log('Mobile: Disease added', restData);
          return success;
        });
    },
    async storeOffline(data) {
      return self.storeModelOffline(CycleDisease, data, async (success: boolean, model: SyncableModel) => {
          const { fileStore } = self.rootStore
          for (let i = 0; i < (data.files || []).length; i++) {
            if (!data.files[i]) continue;
            await fileStore.storeOffline({ fileable_id: model.getKey(), ...data.files[i] })
          }
        })
        .then((success: boolean) => {
          const { files, ...restData } = data;
          if (success) JalaAnalytic.log('Mobile: Disease added', restData);
          return success;
        });
    },
    async update(model: SyncableModel, data) {
      if (self.rootStore.appStore?.isOffline)
        return this.updateOffline(model, data)
      return self.updateModel(CycleDisease, model, data, false, async (success: boolean, model: SyncableModel) => {
          const { fileStore } = self.rootStore
          for (let i = 0; i < (data.files || []).length; i++) {
            if (!data.files[i]) continue;
            const file = model.getRelation('files')
              .find((f) => f.getKey() === data.files[i].id)
            if (file) {
              if (data.files[i]._action === 'delete')
                await fileStore.delete(file)
              else await fileStore.update(file, { fileable_id: model.getKey(), ...data.files[i] })
            } else await fileStore.store({ fileable_id: model.getKey(), ...data.files[i] })
          }
        })
        .then((success: boolean) => {
          const { files, ...restData } = data;
          if (success) JalaAnalytic.log('Mobile: Disease updated', restData);
          return success;
        });
    },
    async updateOffline(model: SyncableModel, data) {
      return self.updateModelOffline(CycleDisease, model, data, async (success: boolean, model: SyncableModel) => {
          const { fileStore } = self.rootStore
          for (let i = 0; i < (data.files || []).length; i++) {
            if (!data.files[i]) continue;
            const file = model.getRelation('files')
              .find((f) => f.getKey() === data.files[i].id)
            if (file) {
              if (data.files[i]._action === 'delete')
                await fileStore.deleteOffline(file)
              else await fileStore.updateOffline(file, { fileable_id: model.getKey(), ...data.files[i] })
            } else await fileStore.storeOffline({ fileable_id: model.getKey(), ...data.files[i] })
          }
        })
        .then((success: boolean) => {
          const { files, ...restData } = data;
          if (success) JalaAnalytic.log('Mobile: Disease updated', restData);
          return success;
        });
    },
    async delete(model: SyncableModel) {
      if (self.rootStore.appStore?.isOffline)
        return this.deleteOffline(model)
      return self.deleteModel(CycleDisease, model, false)
        .then((success: boolean) => {
          if (success) JalaAnalytic.log('Mobile: Disease deleted', model.toArray());
          return success;
        });
    },
    async deleteOffline(model: SyncableModel) {
      return self.deleteModelOffline(CycleDisease, model, false)
        .then((success: boolean) => {
          if (success) JalaAnalytic.log('Mobile: Disease deleted', model.toArray());
          return success;
        });
    },
  }))

type CycleDiseaseStoreType = Instance<typeof CycleDiseaseStoreModel>
export interface CycleDiseaseStore extends CycleDiseaseStoreType {}
type CycleDiseaseStoreSnapshotType = SnapshotOut<typeof CycleDiseaseStoreModel>
export interface CycleDiseaseStoreSnapshot extends CycleDiseaseStoreSnapshotType {}
export const createCycleDiseaseStoreDefaultModel = () => types.optional(CycleDiseaseStoreModel, {})
